<template>
  <div
    class="input-signature-wrapper"
    :class="{
      'has-warning': isRequired && !hasSignature,
      'has-success': isRequired && hasSignature,
      'is-disabled': isDisabled,
    }"
  >
    <label v-if="label && label !== ''" class="input-label" :for="elemId">
      <i v-if="isRequired && !hasSignature" class="pi pi-exclamation-triangle text-warning"></i>
      <i v-if="isRequired && hasSignature" class="pi pi-check text-success"></i>
      <span v-if="isRequired" class="required-asterisk">*</span>
      <span>{{ label }}</span>
    </label>

    <!-- Drawn signature -->
    <div v-if="mode === 'desenhada'" class="signature-surface">
      <canvas
        :id="elemId"
        ref="canvasEl"
        class="signature-canvas"
        :data-testid="dataTestid || elemId"
        @pointerdown="onPointerDown"
        @pointermove="onPointerMove"
        @pointerup="onPointerUp"
        @pointercancel="onPointerUp"
        @pointerleave="onPointerUp"
      ></canvas>

      <div v-if="!hasStrokes" class="signature-hint">
        {{ hint }}
      </div>

      <div class="signature-baseline"></div>
    </div>

    <!-- Accessibility fallback: type the full name -->
    <div v-else class="signature-typed">
      <input
        :id="elemId"
        v-model="typedName"
        type="text"
        class="signature-typed-input"
        :placeholder="typedPlaceholder"
        :disabled="isDisabled"
        :data-testid="(dataTestid || elemId) + '-typed'"
        @input="onTypedInput"
      />
    </div>

    <div class="signature-actions">
      <button
        type="button"
        class="signature-action"
        :disabled="isDisabled || !hasStrokes"
        :data-testid="(dataTestid || elemId) + '-clear'"
        @click="clear"
      >
        {{ clearLabel }}
      </button>

      <button
        type="button"
        class="signature-action signature-action-link"
        :disabled="isDisabled"
        :data-testid="(dataTestid || elemId) + '-mode'"
        @click="toggleMode"
      >
        {{ mode === 'desenhada' ? typedModeLabel : drawnModeLabel }}
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
/**
 * InputSignature — capture a handwritten signature with a finger on a
 * touchscreen, or with a mouse or trackpad on a laptop.
 *
 * ONE CODE PATH FOR EVERY DEVICE: Pointer Events. Handling mouse and touch
 * separately means two behaviours that drift, and on hybrid devices both fire.
 * setPointerCapture keeps the stroke alive when the finger leaves the canvas
 * mid-signature, which on a small screen is most of the time.
 *
 * TOUCH-ACTION: NONE IS LOAD-BEARING, not styling. Without it the browser
 * treats a finger drag as a scroll gesture and the canvas never receives the
 * move events: the component looks dead on exactly the device it exists for.
 *
 * THE CANVAS IS SIZED IN DEVICE PIXELS. A canvas whose backing store matches
 * its CSS box is blurry at any devicePixelRatio above 1, which is every phone.
 * Resizes replay the captured points rather than scaling the old bitmap,
 * because scaling pixels blurs and replaying is exact.
 *
 * TYPED FALLBACK: requiring a drawn stroke from everyone excludes people using
 * a keyboard or a screen reader. They type their full name instead, and the
 * record says which method was used — the two are not the same evidence and
 * the audit must be able to tell them apart.
 *
 * The verdict on whether something counts as a signature lives in
 * signatureGeometry.ts, which is pure and tested without a browser.
 */
import { ref, computed, onMounted, onBeforeUnmount, watch, nextTick } from 'vue';
import {
  metrics as computeMetrics,
  isMeaningful,
  scaleStrokes,
  strokeToOps,
  EMPTY_METRICS,
  type SignatureMetrics,
  type SignaturePoint,
  type SignatureStroke,
} from './signatureGeometry';

type SignatureMode = 'desenhada' | 'digitada';

const props = withDefaults(
  defineProps<{
    modelValue?: string;
    elemId?: string;
    label?: string;
    required?: boolean;
    disabled?: boolean;
    dataTestid?: string;
    hint?: string;
    clearLabel?: string;
    typedModeLabel?: string;
    drawnModeLabel?: string;
    typedPlaceholder?: string;
    penColor?: string;
    penWidth?: number;
  }>(),
  {
    modelValue: '',
    elemId: 'assinatura',
    label: '',
    required: false,
    disabled: false,
    dataTestid: '',
    hint: 'Assine aqui com o dedo ou o mouse',
    clearLabel: 'Limpar',
    typedModeLabel: 'Não consigo desenhar',
    drawnModeLabel: 'Desenhar assinatura',
    typedPlaceholder: 'Digite seu nome completo',
    penColor: '#111827',
    penWidth: 2,
  },
);

const emit = defineEmits<{
  'update:modelValue': [value: string];
  /** Fires with every completed stroke so a form can gate its submit. */
  change: [payload: { value: string; method: SignatureMode; metrics: SignatureMetrics }];
}>();

const canvasEl = ref<HTMLCanvasElement | null>(null);
const strokes = ref<SignatureStroke[]>([]);
const current = ref<SignatureStroke>([]);
const mode = ref<SignatureMode>('desenhada');
const typedName = ref('');
const startedAt = ref(0);

let resizeObserver: ResizeObserver | null = null;

const isRequired = computed(() => props.required === true);
const isDisabled = computed(() => props.disabled === true);
const hasStrokes = computed(() =>
  mode.value === 'desenhada' ? strokes.value.length > 0 : typedName.value.trim() !== '',
);

const currentMetrics = computed<SignatureMetrics>(() =>
  mode.value === 'desenhada' ? computeMetrics(strokes.value) : { ...EMPTY_METRICS },
);

const hasSignature = computed(() =>
  mode.value === 'desenhada'
    ? isMeaningful(currentMetrics.value)
    : typedName.value.trim().length >= 3,
);

// =========================================================================
// Canvas plumbing
// =========================================================================

function context(): CanvasRenderingContext2D | null {
  return canvasEl.value?.getContext('2d') ?? null;
}

/**
 * Match the backing store to the CSS box times the device pixel ratio, then
 * replay every captured point at the new scale.
 */
function resizeCanvas(): void {
  const canvas = canvasEl.value;
  if (!canvas) return;

  const rect = canvas.getBoundingClientRect();
  if (rect.width === 0 || rect.height === 0) return;

  const ratio = window.devicePixelRatio || 1;
  const nextWidth = Math.round(rect.width * ratio);
  const nextHeight = Math.round(rect.height * ratio);

  if (canvas.width === nextWidth && canvas.height === nextHeight) return;

  const factorX = canvas.width > 0 ? nextWidth / canvas.width : 1;
  const factorY = canvas.height > 0 ? nextHeight / canvas.height : 1;

  canvas.width = nextWidth;
  canvas.height = nextHeight;

  if (canvas.width > 0 && (factorX !== 1 || factorY !== 1)) {
    strokes.value = scaleStrokes(strokes.value, factorX, factorY);
  }

  redraw();
}

function redraw(): void {
  const canvas = canvasEl.value;
  const ctx = context();
  if (!canvas || !ctx) return;

  ctx.clearRect(0, 0, canvas.width, canvas.height);

  const ratio = window.devicePixelRatio || 1;
  ctx.lineWidth = props.penWidth * ratio;
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';
  ctx.strokeStyle = props.penColor;

  for (const stroke of [...strokes.value, current.value]) {
    const ops = strokeToOps(stroke);
    if (ops.length === 0) continue;

    ctx.beginPath();
    for (const op of ops) {
      if (op.op === 'move') ctx.moveTo(op.x, op.y);
      else if (op.op === 'line') ctx.lineTo(op.x, op.y);
      else ctx.quadraticCurveTo(op.cx, op.cy, op.x, op.y);
    }
    ctx.stroke();
  }
}

function pointFrom(event: PointerEvent): SignaturePoint | null {
  const canvas = canvasEl.value;
  if (!canvas) return null;

  const rect = canvas.getBoundingClientRect();
  const ratio = window.devicePixelRatio || 1;

  return {
    x: (event.clientX - rect.left) * ratio,
    y: (event.clientY - rect.top) * ratio,
    t: startedAt.value === 0 ? 0 : performance.now() - startedAt.value,
  };
}

// =========================================================================
// Pointer handling
// =========================================================================

const drawing = ref(false);

function onPointerDown(event: PointerEvent): void {
  if (isDisabled.value) return;

  event.preventDefault();
  if (startedAt.value === 0) startedAt.value = performance.now();

  // Keeps the stroke alive when the finger wanders outside the canvas.
  canvasEl.value?.setPointerCapture?.(event.pointerId);

  drawing.value = true;
  const point = pointFrom(event);
  current.value = point ? [point] : [];
  redraw();
}

function onPointerMove(event: PointerEvent): void {
  if (!drawing.value || isDisabled.value) return;

  event.preventDefault();
  const point = pointFrom(event);
  if (point) {
    current.value = [...current.value, point];
    redraw();
  }
}

function onPointerUp(event: PointerEvent): void {
  if (!drawing.value) return;

  drawing.value = false;
  canvasEl.value?.releasePointerCapture?.(event.pointerId);

  if (current.value.length > 0) {
    strokes.value = [...strokes.value, current.value];
  }
  current.value = [];

  redraw();
  publish();
}

// =========================================================================
// Value
// =========================================================================

/**
 * The emitted value is a PNG data URL, or '' while the drawing does not yet
 * count as a signature. Emitting a half-drawn stroke would let a form think it
 * had consent from a scroll gesture.
 */
function publish(): void {
  let value = '';

  if (mode.value === 'desenhada') {
    if (hasSignature.value && canvasEl.value) {
      value = canvasEl.value.toDataURL('image/png');
    }
  } else if (hasSignature.value) {
    // The typed fallback produces a PNG too, so storage and server-side
    // validation have ONE shape instead of a string-or-image union that every
    // reader would have to branch on. Which method was used is recorded
    // separately, because the two are not equivalent evidence.
    value = renderTypedName(typedName.value.trim());
  }

  emit('update:modelValue', value);
  emit('change', { value, method: mode.value, metrics: currentMetrics.value });
}

/** Draw the typed name onto an offscreen canvas and return it as a PNG. */
function renderTypedName(name: string): string {
  const ratio = window.devicePixelRatio || 1;
  const canvas = document.createElement('canvas');
  canvas.width = Math.round(600 * ratio);
  canvas.height = Math.round(160 * ratio);

  const ctx = canvas.getContext('2d');
  if (!ctx) return '';

  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  ctx.fillStyle = props.penColor;
  ctx.font = `${Math.round(44 * ratio)}px cursive, system-ui`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(name, canvas.width / 2, canvas.height / 2, canvas.width * 0.9);

  return canvas.toDataURL('image/png');
}

function onTypedInput(): void {
  publish();
}

function clear(): void {
  strokes.value = [];
  current.value = [];
  typedName.value = '';
  startedAt.value = 0;
  redraw();
  publish();
}

function toggleMode(): void {
  mode.value = mode.value === 'desenhada' ? 'digitada' : 'desenhada';
  clear();
  if (mode.value === 'desenhada') {
    nextTick(() => resizeCanvas());
  }
}

defineExpose({ clear, metrics: currentMetrics });

// =========================================================================
// Lifecycle
// =========================================================================

onMounted(() => {
  resizeCanvas();

  if (typeof ResizeObserver !== 'undefined' && canvasEl.value) {
    resizeObserver = new ResizeObserver(() => resizeCanvas());
    resizeObserver.observe(canvasEl.value);
  }
});

onBeforeUnmount(() => {
  resizeObserver?.disconnect();
  resizeObserver = null;
});

// A parent clearing the bound value (a reset) must clear the canvas too.
watch(
  () => props.modelValue,
  (next) => {
    if (next === '' && hasStrokes.value) {
      strokes.value = [];
      current.value = [];
      typedName.value = '';
      startedAt.value = 0;
      redraw();
    }
  },
);
</script>

<style scoped>
.input-signature-wrapper {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin-bottom: 1rem;
}

.input-label {
  display: flex;
  align-items: center;
  gap: 0.25rem;
  font-weight: 500;
  color: var(--p-text-color);
}

.required-asterisk {
  color: var(--p-red-500);
  margin-right: 0.25rem;
}

.text-warning {
  color: var(--p-yellow-500);
}

.text-success {
  color: var(--p-green-500);
}

.signature-surface {
  position: relative;
  border: 1px solid var(--p-inputtext-border-color, #d1d5db);
  border-radius: 6px;
  background: var(--p-inputtext-background, #fff);
  overflow: hidden;
}

.signature-canvas {
  display: block;
  width: 100%;
  height: 180px;
  /*
   * NOT styling: without this the browser claims the finger drag as a scroll
   * and the canvas never sees pointermove.
   */
  touch-action: none;
  cursor: crosshair;
}

.signature-hint {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;
  color: var(--p-text-muted-color, #9ca3af);
  font-size: 0.9rem;
}

.signature-baseline {
  position: absolute;
  left: 12%;
  right: 12%;
  bottom: 42px;
  border-bottom: 1px dashed var(--p-text-muted-color, #d1d5db);
  pointer-events: none;
}

.signature-typed-input {
  width: 100%;
  padding: 0.75rem;
  border: 1px solid var(--p-inputtext-border-color, #d1d5db);
  border-radius: 6px;
  font-family: cursive, system-ui;
  font-size: 1.25rem;
}

.signature-actions {
  display: flex;
  gap: 0.75rem;
  align-items: center;
}

.signature-action {
  background: none;
  border: 1px solid var(--p-inputtext-border-color, #d1d5db);
  border-radius: 6px;
  padding: 0.35rem 0.75rem;
  cursor: pointer;
  color: var(--p-text-color);
}

.signature-action:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

.signature-action-link {
  border: none;
  text-decoration: underline;
  padding-left: 0;
}

.has-warning .signature-surface {
  border-color: var(--p-yellow-500);
}

.has-success .signature-surface {
  border-color: var(--p-green-500);
}

.is-disabled {
  opacity: 0.5;
  pointer-events: none;
}
</style>
